import type { CountryPracticeConfig } from '../config';

/**
 * Tap-the-country practice mode — endless prompt → click-on-correct-country
 * loop over the global pool. First mode that exercises the `click-in-region`
 * interaction axis.
 *
 * Visual model: solid gray country fills on the dark base sphere. No photo
 * texture — saves the bandwidth and stops the texture from giving away
 * shapes via coastline coloring. Borders on top in full opacity so the
 * country edges read clearly.
 *
 * Audio: bright C-major chord on correct, low single tone on wrong.
 */
export const countriesWorldPractice: CountryPracticeConfig = {
  id: 'countries-world-practice',
  scope: { entity: 'countries', region: 'world' },
  format: 'practice',
  interaction: 'click-in-region',

  displayName: 'World Countries - Practice',
  shortName: 'World Countries',

  // Flat gray globe: photo off, fills opaque, borders crisp. Hover gives
  // a subtle highlight so the user can see what they'd click before
  // committing. The route also passes `skipTexture: true` to MapEngine
  // so the texture never even gets fetched.
  layers: {
    texture: { visible: false, opacity: 0 },
    borders: { visible: true, opacity: 1.0 },
    fills: { visible: true, opacity: 1.0 },
    hover: true,
  },
  // Texture is intentionally locked off — turning it on mid-game would
  // change the difficulty character entirely. Other layers stay tweakable.
  userMayToggle: {
    texture: false,
    borders: true,
    fills: false,
    hover: true,
  },

  unitSystem: 'miles',
  // UN-members baseline matches the standard "193 countries" list most
  // players know from Seterra etc. Disputed/unrecognized territories
  // (Taiwan, Vatican, Palestine, Western Sahara, Kosovo) are dropped
  // from the practice pool by default — keeps the world view "clean"
  // and matches the Seterra-style world quiz expectation.
  countryFilter: { setName: 'un-members' },
  userMayChangeFilter: true,
  audio: {
    // Bright C-major chord (C5, E5, G5) — same "great guess" feel as
    // the city-distance mode's pinpoint reward, intentionally familiar.
    correctFrequencies: [523.25, 659.25, 783.99],
    // A3 — low, neutral, not punishing. Avoids the "buzzer" feel that
    // makes practice modes feel hostile.
    incorrectFrequencies: 220,
  },
};
